import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Loader2, Users, User, Sparkles, Presentation, GraduationCap, CalendarClock,
  Monitor, MapPin, FileText, ArrowLeft, BadgeEuro, CheckCircle2,
} from "lucide-react";
import { myApi } from "@/my/api";

const INK = "#0F1829";
const eur = (v) =>
  (v === null || v === undefined || isNaN(Number(v)))
    ? "—"
    : Number(v).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const NEC_ICON = { motivar: Presentation, competencias: GraduationCap, continuo: CalendarClock };

function Choice({ active, onClick, icon: Icon, title, desc, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-2xl border p-5 transition-all w-full ${
        active ? "border-[#0F1829] bg-[#0F1829] text-white shadow-lg" : "border-[#E5E1D6] bg-white hover:border-[#0F1829]/40 hover:shadow"
      }`}
    >
      {Icon && <Icon className={`w-6 h-6 mb-3 ${active ? "text-white" : "text-[#4A5E2E]"}`} />}
      <div className="font-semibold text-base">{title}</div>
      {desc && <div className={`text-sm mt-1 ${active ? "text-white/70" : "text-[#6B7280]"}`}>{desc}</div>}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wide text-[#6B7280] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full rounded-xl border border-[#E5E1D6] bg-white px-3.5 py-2.5 text-sm text-[#0F1829] outline-none focus:border-[#0F1829] transition-colors";

export default function MyEventos() {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("proposta");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // seleções
  const [tipo, setTipo] = useState(null);            // evento | individual
  const [necessidade, setNecessidade] = useState(null);
  const [servico, setServico] = useState(null);      // executivo | life
  const [f, setF] = useState({
    orador: "IHTP", coach: "IHTP", bucket: "", nr_sessoes: 1,
    online: false, nr_pessoas: "", data_evento: "", local: "",
    zona_porto: true, km: "", alojamento: false, periodicidade: "mensal",
    valor_investimento: "", empresa: "", id_formador: null, formador_nome: "",
  });
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const [proposta, setProposta] = useState(null);
  const [calc, setCalc] = useState(false);
  const [formadores, setFormadores] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [leadState, setLeadState] = useState(null); // null | "loading" | "done" | "error"
  const [saveModo, setSaveModo] = useState("mais_tempo"); // agora | mais_tempo
  const [dataDecisao, setDataDecisao] = useState("");
  const [saveState, setSaveState] = useState(null); // null | loading | done | error
  const [readOnly, setReadOnly] = useState(false);
  const [propostaEstado, setPropostaEstado] = useState(null);
  const [encomendas, setEncomendas] = useState([]);

  const maxDataDecisao = (() => { const d = new Date(); d.setDate(d.getDate() + 90); return d.toISOString().slice(0, 10); })();
  const minDataDecisao = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();

  async function guardarProposta() {
    if (saveModo === "mais_tempo" && !dataDecisao) { setSaveState("error"); return; }
    setSaveState("loading");
    try {
      const body = {
        proposta: buildPayload(),
        modo: saveModo,
        data_decisao: saveModo === "mais_tempo" ? dataDecisao : null,
      };
      if (editId) await myApi.propostaEditar(editId, body);
      else await myApi.propostaGuardar(body);
      setSaveState("done");
      setTimeout(() => { window.location.href = "/my/propostas"; }, 900);
    } catch (e) { setSaveState("error"); }
  }


  async function pedirContacto() {
    setLeadState("loading");
    try {
      await myApi.eventosLead(buildPayload());
      setLeadState("done");
    } catch (e) {
      setErr(e.message || "Não foi possível registar o pedido de contacto.");
      setLeadState("error");
    }
  }

  useEffect(() => {
    myApi.eventosConfig()
      .then((d) => setConfig(d))
      .catch((e) => setErr(e.message || "Erro ao carregar configuração."))
      .finally(() => setLoading(false));
  }, []);

  // Carrega formadores IHTP quando o orador é IHTP (evento)
  useEffect(() => {
    if (tipo === "evento" && f.orador === "IHTP" && formadores.length === 0) {
      myApi.eventosFormadores().then((d) => setFormadores(d.formadores || [])).catch(() => {});
    }
  }, [tipo, f.orador]); // eslint-disable-line

  // Modo edição: carrega a config guardada da proposta (?proposta=id)
  useEffect(() => {
    if (!editId) return;
    myApi.propostaGet(editId)
      .then((d) => {
        const cfg = d.config || {};
        if (cfg.tipo) setTipo(cfg.tipo);
        if (cfg.necessidade) setNecessidade(cfg.necessidade);
        if (cfg.servico) setServico(cfg.servico);
        setF((p) => ({
          ...p,
          orador: cfg.orador || "IHTP", coach: cfg.coach || "IHTP",
          bucket: cfg.bucket || "", nr_sessoes: cfg.nr_sessoes || 1,
          online: !!cfg.online, nr_pessoas: cfg.nr_pessoas ?? "",
          data_evento: cfg.data_evento || "", local: cfg.local || "",
          zona_porto: cfg.zona_porto ?? true, km: cfg.km ?? "",
          alojamento: !!cfg.alojamento, periodicidade: cfg.periodicidade || "mensal",
          valor_investimento: cfg.valor_investimento ?? "", empresa: cfg.empresa || "",
          id_formador: cfg.id_formador || null, formador_nome: cfg.formador_nome || "",
        }));
        const va = d.proposta_meta?.ValidaAte;
        if (va) { setSaveModo("mais_tempo"); setDataDecisao(String(va).slice(0, 10)); }
        setReadOnly(d.proposta_meta?.editavel === false);
        setPropostaEstado(d.proposta_meta?.Estado || null);
        setEncomendas(d.proposta_meta?.encomendas || []);
      })
      .catch((e) => setErr(e.message || "Não foi possível carregar a proposta para edição."));
  }, [editId]); // eslint-disable-line

  const recorrente = tipo === "evento" && necessidade === "continuo";
  const isMultiplo = recorrente || (tipo === "individual" && Number(f.nr_sessoes) >= 2);

  // buckets disponíveis (evento)
  const necObj = useMemo(
    () => (config?.necessidades || []).find((n) => n.key === necessidade),
    [config, necessidade]
  );
  const eventoBuckets = necObj?.buckets || [];

  // opções executivo (preço único, sem escolha de coach)
  const execOptions = config?.individual?.executivo_options || [];

  // reset dependentes
  useEffect(() => { setProposta(null); setLeadState(null); }, [tipo, necessidade, servico, f]);
  useEffect(() => {
    if (tipo === "evento" && eventoBuckets.length && !eventoBuckets.includes(f.bucket)) {
      upd("bucket", eventoBuckets[0]);
    }
  }, [tipo, necessidade]); // eslint-disable-line

  useEffect(() => {
    if (tipo === "individual" && servico === "executivo" && execOptions.length
        && !execOptions.some((o) => o.bucket === f.bucket)) {
      upd("bucket", execOptions[0].bucket);
    }
  }, [tipo, servico]); // eslint-disable-line

  function buildPayload() {
    const base = {
      tipo,
      online: f.online,
      data_evento: f.data_evento || null,
      empresa: f.empresa || null,
      valor_investimento: f.valor_investimento ? Number(f.valor_investimento) : null,
    };
    if (!f.online) {
      base.local = f.local || null;
      base.zona_porto = f.zona_porto;
      base.km = f.zona_porto ? null : (f.km ? Number(f.km) : 0);
      base.alojamento = f.alojamento;
    }
    if (tipo === "evento") {
      return {
        ...base, necessidade, bucket: necessidade === "motivar" ? "1h" : f.bucket, orador: f.orador,
        nr_pessoas: f.nr_pessoas ? Number(f.nr_pessoas) : null,
        periodicidade: recorrente ? f.periodicidade : null,
        id_formador: f.orador === "IHTP" ? (f.id_formador || null) : null,
        formador_nome: f.orador === "IHTP" ? (f.formador_nome || null) : null,
      };
    }
    // individual
    if (servico === "life") {
      return { ...base, servico, coach: f.coach, nr_sessoes: Number(f.nr_sessoes || 1) };
    }
    return { ...base, servico, coach: f.coach, bucket: f.bucket, nr_sessoes: Number(f.nr_sessoes || 1) };
  }

  async function verProposta() {
    setCalc(true); setErr(""); setProposta(null);
    try {
      const d = await myApi.eventosProposta(buildPayload());
      setProposta(d.proposta);
    } catch (e) {
      setErr(e.message || "Não foi possível gerar a proposta.");
    } finally {
      setCalc(false);
    }
  }

  async function descarregarPdf() {
    setPdfLoading(true); setErr("");
    try {
      const res = await fetch(`/api/my/eventos/proposta/pdf`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("Erro ao gerar PDF.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      setErr(e.message || "Erro ao gerar PDF.");
    } finally {
      setPdfLoading(false);
    }
  }

  // validação mínima para habilitar cálculo
  const canCalc = useMemo(() => {
    if (!tipo) return false;
    if (tipo === "evento") return !!necessidade && (necessidade === "motivar" || !!f.bucket) && Number(f.nr_pessoas) >= 1;
    if (tipo === "individual") {
      if (servico === "executivo") return !!f.bucket;
      if (servico === "life") return Number(f.nr_sessoes) >= 1;
    }
    return false;
  }, [tipo, necessidade, servico, f.bucket, f.nr_sessoes]);

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#0F1829]" /></div>;
  }

  return (
    <div data-testid="my-eventos-page" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-4xl text-[#0F1829] mb-1" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
          Proposta de Investimento
        </h1>
        <p className="text-sm text-[#6B7280]">
          Configure a intervenção ideal para a sua empresa e gere uma proposta em segundos.
        </p>
      </div>

      <div className="flex gap-1 p-1 rounded-full bg-[#F0EBDF] w-fit" data-testid="propostas-tabs">
        <Link to="/my/eventos" data-testid="tab-nova-proposta"
          className="px-4 py-2 rounded-full text-sm font-medium bg-[#0F1829] text-white">Nova proposta</Link>
        <Link to="/my/propostas" data-testid="tab-minhas-propostas"
          className="px-4 py-2 rounded-full text-sm font-medium text-[#6B7280] hover:text-[#0F1829]">As minhas propostas</Link>
      </div>

      {err && <div data-testid="my-eventos-error" className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{err}</div>}

      {/* Passo 1 — Tipo */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-[#0F1829]">1. O que procura?</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <Choice testid="tipo-evento" active={tipo === "evento"} icon={Users}
            title="Evento para a equipa" desc="Palestras e workshops para o seu grupo"
            onClick={() => { setTipo("evento"); setServico(null); }} />
          <Choice testid="tipo-individual" active={tipo === "individual"} icon={User}
            title="Apoio Individual" desc="Coaching Executivo ou Life Coaching"
            onClick={() => { setTipo("individual"); setNecessidade(null); }} />
        </div>
      </section>

      {/* Passo 2a — Necessidade (evento) */}
      {tipo === "evento" && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-[#0F1829]">2. Qual a necessidade principal?</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {(config.necessidades || []).map((n) => (
              <Choice key={n.key} testid={`nec-${n.key}`} active={necessidade === n.key}
                icon={NEC_ICON[n.key] || Sparkles} title={n.label}
                onClick={() => setNecessidade(n.key)} />
            ))}
          </div>
        </section>
      )}

      {/* Passo 2b — Serviço (individual) */}
      {tipo === "individual" && (
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-[#0F1829]">2. Tipo de apoio</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {(config.individual?.tipos || []).map((t) => (
              <Choice key={t.key} testid={`serv-${t.key}`} active={servico === t.key}
                icon={t.key === "executivo" ? BadgeEuro : Sparkles} title={t.label}
                onClick={() => setServico(t.key)} />
            ))}
          </div>
        </section>
      )}

      {/* Passo 3 — Detalhes */}
      {((tipo === "evento" && necessidade) || (tipo === "individual" && servico)) && (
        <section className="space-y-4 rounded-2xl border border-[#E5E1D6] bg-[#FBFAF7] p-5">
          <h2 className="text-base font-semibold text-[#0F1829]">3. Detalhes</h2>
          <div className="grid sm:grid-cols-2 gap-4">

            {/* Orador (evento) */}
            {tipo === "evento" && (
              <Field label="Orador">
                <select data-testid="sel-orador" className={inputCls} value={f.orador} onChange={(e) => upd("orador", e.target.value)}>
                  {(config.oradores || []).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </Field>
            )}

            {/* Duração (evento) — oculta em "motivar" (é sempre até 1h) */}
            {tipo === "evento" && necessidade !== "motivar" && (
              <Field label="Duração da intervenção">
                <select data-testid="sel-bucket" className={inputCls} value={f.bucket} onChange={(e) => upd("bucket", e.target.value)}>
                  {eventoBuckets.map((b) => (
                    <option key={b} value={b}>{b === "1h" ? "Até 1 hora" : b === "4h" ? "Até 4 horas" : "Até 8 horas (1 dia)"}</option>
                  ))}
                </select>
              </Field>
            )}

            {/* Nº pessoas (evento) — obrigatório */}
            {tipo === "evento" && (
              <Field label="Nº de participantes *">
                <input data-testid="inp-pessoas" type="number" min="1" required className={inputCls} value={f.nr_pessoas}
                  onChange={(e) => upd("nr_pessoas", e.target.value)} placeholder="Ex.: 30" />
              </Field>
            )}

            {/* Periodicidade (evento contínuo) */}
            {recorrente && (
              <Field label="Periodicidade (programa contínuo)">
                <select data-testid="sel-periodicidade" className={inputCls} value={f.periodicidade} onChange={(e) => upd("periodicidade", e.target.value)}>
                  {(config.periodicidades || []).map((p) => (
                    <option key={p.key} value={p.key}>{p.label} ({p.intervencoes_ano}x/ano)</option>
                  ))}
                </select>
              </Field>
            )}

            {/* Coach (só Life Coaching) */}
            {tipo === "individual" && servico === "life" && (
              <Field label="Coach">
                <select data-testid="sel-coach" className={inputCls} value={f.coach} onChange={(e) => upd("coach", e.target.value)}>
                  {(config.individual?.coaches || []).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </Field>
            )}

            {/* Formato executivo */}
            {tipo === "individual" && servico === "executivo" && (
              <Field label="Formato">
                <select data-testid="sel-exec-bucket" className={inputCls} value={f.bucket} onChange={(e) => upd("bucket", e.target.value)}>
                  {execOptions.map((o) => (
                    <option key={o.bucket} value={o.bucket}>{o.bucket_label} — {eur(o.valor_com_iva)}</option>
                  ))}
                </select>
                {execOptions.length === 0 && <span className="text-xs text-red-600">Sem formatos disponíveis.</span>}
              </Field>
            )}

            {/* Nº de vezes / sessões (individual) */}
            {tipo === "individual" && (servico === "life" || servico === "executivo") && (
              <Field label={servico === "life" ? "Nº de sessões" : "Nº de vezes"}>
                <input data-testid="inp-sessoes" type="number" min="1" className={inputCls} value={f.nr_sessoes}
                  onChange={(e) => upd("nr_sessoes", e.target.value)} />
              </Field>
            )}

            {/* Modalidade */}
            <Field label="Modalidade">
              <div className="flex gap-2">
                <button type="button" data-testid="mod-presencial" onClick={() => upd("online", false)}
                  className={`flex-1 rounded-xl border px-3 py-2.5 text-sm flex items-center justify-center gap-2 ${!f.online ? "border-[#0F1829] bg-[#0F1829] text-white" : "border-[#E5E1D6] bg-white text-[#0F1829]"}`}>
                  <MapPin className="w-4 h-4" /> Presencial
                </button>
                <button type="button" data-testid="mod-online" onClick={() => upd("online", true)}
                  className={`flex-1 rounded-xl border px-3 py-2.5 text-sm flex items-center justify-center gap-2 ${f.online ? "border-[#0F1829] bg-[#0F1829] text-white" : "border-[#E5E1D6] bg-white text-[#0F1829]"}`}>
                  <Monitor className="w-4 h-4" /> Online
                </button>
              </div>
            </Field>

            {/* Data */}
            <Field label={isMultiplo ? "Data prevista de início" : "Data prevista"}>
              <input data-testid="inp-data" type="date" className={inputCls} value={f.data_evento} onChange={(e) => upd("data_evento", e.target.value)} />
            </Field>

            {/* Presencial: local + zona + km + alojamento */}
            {!f.online && (
              <>
                <Field label="Local">
                  <input data-testid="inp-local" className={inputCls} value={f.local} onChange={(e) => upd("local", e.target.value)} placeholder="Cidade / morada" />
                </Field>
                <Field label="Localização">
                  <select data-testid="sel-zona" className={inputCls} value={f.zona_porto ? "porto" : "fora"} onChange={(e) => upd("zona_porto", e.target.value === "porto")}>
                    <option value="porto">Zona do Porto (deslocação oferecida)</option>
                    <option value="fora">Fora do Porto</option>
                  </select>
                </Field>
                {!f.zona_porto && (
                  <Field label="Distância ida + volta (km) desde IHTP Excellence Center Porto">
                    <input data-testid="inp-km" type="number" min="0" className={inputCls} value={f.km} onChange={(e) => upd("km", e.target.value)} placeholder="Ex.: 120" />
                  </Field>
                )}
                <Field label="Alojamento">
                  <label className="flex items-center gap-2 text-sm text-[#0F1829] py-2.5">
                    <input data-testid="chk-alojamento" type="checkbox" checked={f.alojamento} onChange={(e) => upd("alojamento", e.target.checked)} />
                    Implica alojamento (por conta do cliente)
                  </label>
                </Field>
              </>
            )}

            {/* Empresa + orçamento */}
            <Field label="Empresa / Cliente">
              <input data-testid="inp-empresa" className={inputCls} value={f.empresa} onChange={(e) => upd("empresa", e.target.value)} placeholder="Nome da empresa" />
            </Field>
            <Field label="Orçamento disponível (opcional)">
              <input data-testid="inp-orcamento" type="number" min="0" className={inputCls} value={f.valor_investimento} onChange={(e) => upd("valor_investimento", e.target.value)} placeholder="€" />
            </Field>
          </div>

          {/* Seletor de Formador IHTP (só evento + Orador IHTP) */}
          {tipo === "evento" && f.orador === "IHTP" && formadores.length > 0 && (
            <div data-testid="formador-picker" className="rounded-xl border border-[#E5E1D6] bg-white p-4">
              <p className="text-sm font-semibold text-[#0F1829] mb-1">Escolha o orador IHTP (opcional)</p>
              <p className="text-xs text-[#6B7280] mb-3">Selecione um formador específico ou deixe a IHTP indicar o mais adequado.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {formadores.map((fm) => {
                  const active = f.id_formador === fm.id_formador;
                  return (
                    <button type="button" key={fm.id_formador} data-testid={`formador-${fm.id_formador}`}
                      onClick={() => {
                        if (active) { upd("id_formador", null); upd("formador_nome", ""); }
                        else { upd("id_formador", fm.id_formador); upd("formador_nome", fm.nome); }
                      }}
                      className={`flex flex-col items-center gap-2 rounded-xl border p-3 transition-all ${
                        active ? "border-[#0F1829] bg-[#0F1829]/5 shadow" : "border-[#E5E1D6] bg-white hover:border-[#0F1829]/40"
                      }`}>
                      <FormadorAvatar nome={fm.nome} url={fm.foto_url} active={active} />
                      <span className="text-xs font-medium text-[#0F1829] text-center leading-tight">{fm.nome}</span>
                    </button>
                  );
                })}
              </div>

              {f.id_formador && (() => {
                const sel = formadores.find((x) => x.id_formador === f.id_formador);
                if (!sel || !(sel.habilitacoes || sel.certificacoes || sel.experiencia || sel.linkedin)) return null;
                return (
                  <div data-testid="formador-cv" className="mt-4 rounded-xl border border-[#E5E1D6] bg-[#FBFAF7] p-4 text-sm text-[#0F1829] space-y-2">
                    <div className="font-semibold">Currículo — {sel.nome}</div>
                    {sel.habilitacoes && <div className="whitespace-pre-line"><span className="text-[#6B7280]">Habilitações:</span> {sel.habilitacoes}</div>}
                    {sel.certificacoes && <div className="whitespace-pre-line"><span className="text-[#6B7280]">Certificações:</span> {sel.certificacoes}</div>}
                    {sel.experiencia && <div className="whitespace-pre-line"><span className="text-[#6B7280]">Experiência:</span> {sel.experiencia}</div>}
                    {sel.linkedin && <div><span className="text-[#6B7280]">LinkedIn:</span> <a href={sel.linkedin} target="_blank" rel="noreferrer" className="text-[#4A5E2E] underline break-all">{sel.linkedin}</a></div>}
                  </div>
                );
              })()}
            </div>
          )}

          <div className="flex justify-end">
            <button data-testid="btn-ver-proposta" disabled={calc} onClick={verProposta}
              className="inline-flex items-center gap-2 rounded-full bg-[#0F1829] text-white px-6 py-3 text-sm font-semibold disabled:opacity-40 hover:bg-[#0F1829]/90 transition-colors">
              {calc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Ver proposta
            </button>
          </div>
        </section>
      )}

      {/* Resultado */}
      {proposta && (
        <section data-testid="my-eventos-resultado" className="rounded-2xl border-2 border-[#0F1829] bg-white overflow-hidden">
          <div className="bg-[#0F1829] text-white px-6 py-4">
            <div className="text-xs uppercase tracking-widest text-white/60">Proposta de Investimento</div>
            <div className="text-lg font-semibold">{proposta.evento_descricao}</div>
            <div className="text-sm text-white/70">{proposta.necessidade_label} · {proposta.bucket_label} · {proposta.online ? "Online" : "Presencial"}</div>
          </div>
          <div className="p-6 space-y-3">
            <Line label={proposta.recorrente ? (proposta.unit_label || "Valor por intervenção") : "Valor da intervenção"} value={eur(proposta.evento_com_iva)} />
            {proposta.recorrente && proposta.desconto_pct ? (
              <Line label={`Desconto por volume (${proposta.desconto_pct}%)`} value={`− ${eur(proposta.desconto_valor)}`} muted />
            ) : null}
            {!proposta.online && (
              <Line label="Custos de deslocação" value={proposta.zona_porto ? "Oferta" : eur(proposta.deslocacao_com_iva)} />
            )}
            {proposta.tipo !== "individual" && (
              <Line label="Reunião preparatória" value="Oferta" />
            )}
            <div className="border-t border-[#E5E1D6] pt-3 flex items-center justify-between">
              <span className="font-semibold text-[#0F1829]">{proposta.total_label || "Total do investimento"} (c/ IVA)</span>
              <span data-testid="proposta-total" className="text-2xl font-bold text-[#0F1829]">{eur(proposta.total_com_iva)}</span>
            </div>
            {proposta.valor_por_pessoa && proposta.nr_pessoas > 1 ? (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#6B7280]">Valor por participante ({proposta.nr_pessoas} pessoas)</span>
                  <span data-testid="proposta-por-pessoa" className="text-[#0F1829] font-medium tabular-nums">{eur(proposta.valor_por_pessoa)}</span>
                </div>
                {proposta.valor_por_pessoa_dia ? (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#6B7280]">Valor por participante / dia (÷ 365)</span>
                    <span data-testid="proposta-por-pessoa-dia" className="text-[#4A5E2E] font-semibold tabular-nums">{eur(proposta.valor_por_pessoa_dia)}</span>
                  </div>
                ) : null}
              </>
            ) : null}
            {proposta.tipo === "individual" && proposta.valor_por_dia ? (
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#6B7280]">Valor por dia (÷ 365)</span>
                <span data-testid="proposta-por-dia" className="text-[#4A5E2E] font-semibold tabular-nums">{eur(proposta.valor_por_dia)}</span>
              </div>
            ) : null}

            <div className="rounded-xl bg-[#FBFAF7] border border-[#E5E1D6] p-4 text-sm text-[#0F1829] space-y-1.5 mt-2">
              <div className="font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#4A5E2E]" /> Condições</div>
              <div>• Pagamento: <b>50%</b> na adjudicação ({eur(proposta.deposito_valor)}) + <b>50%</b> até à véspera do início do serviço ({eur(proposta.restante_valor)}).</div>
              {proposta.online
                ? <div>• Modalidade Online: sem custos de deslocação nem estadia.</div>
                : (proposta.zona_porto
                    ? <div>• Deslocação: oferta (zona do Porto).</div>
                    : <div>• Deslocação: ida e volta desde a Rua de Camões, 117, Porto, a 0,49 €/km (c/ IVA).</div>)}
              {(!proposta.online && proposta.alojamento) && <div>• Alojamento: por conta do cliente.</div>}
            </div>

            {proposta.formador_cv && (proposta.formador_cv.habilitacoes || proposta.formador_cv.certificacoes || proposta.formador_cv.experiencia || proposta.formador_cv.linkedin) && (
              <div data-testid="proposta-orador-cv" className="rounded-xl bg-white border border-[#E5E1D6] p-4 text-sm text-[#0F1829] space-y-2 mt-2">
                <div className="font-semibold">Perfil do Orador{proposta.formador_cv.nome ? ` — ${proposta.formador_cv.nome}` : (proposta.formador_nome ? ` — ${proposta.formador_nome}` : "")}</div>
                {proposta.formador_cv.habilitacoes && <div className="whitespace-pre-line"><span className="text-[#6B7280]">Habilitações:</span> {proposta.formador_cv.habilitacoes}</div>}
                {proposta.formador_cv.certificacoes && <div className="whitespace-pre-line"><span className="text-[#6B7280]">Certificações:</span> {proposta.formador_cv.certificacoes}</div>}
                {proposta.formador_cv.experiencia && <div className="whitespace-pre-line"><span className="text-[#6B7280]">Experiência:</span> {proposta.formador_cv.experiencia}</div>}
                {proposta.formador_cv.linkedin && <div><span className="text-[#6B7280]">LinkedIn:</span> <a href={proposta.formador_cv.linkedin} target="_blank" rel="noreferrer" className="text-[#4A5E2E] underline break-all">{proposta.formador_cv.linkedin}</a></div>}
              </div>
            )}

            {proposta.valor_investimento ? (
              <div className={`text-sm ${Number(proposta.valor_investimento) >= Number(proposta.total_com_iva) ? "text-[#4A5E2E]" : "text-amber-700"}`}>
                {Number(proposta.valor_investimento) >= Number(proposta.total_com_iva)
                  ? `✓ Enquadra-se no orçamento indicado (${eur(proposta.valor_investimento)}).`
                  : `⚠ Excede o orçamento indicado (${eur(proposta.valor_investimento)}) em ${eur(Number(proposta.total_com_iva) - Number(proposta.valor_investimento))}.`}
              </div>
            ) : null}

            <div className="flex justify-end pt-2">
              <button data-testid="btn-download-pdf" onClick={descarregarPdf} disabled={pdfLoading}
                className="inline-flex items-center gap-2 rounded-full bg-[#4A5E2E] text-white px-6 py-3 text-sm font-semibold hover:bg-[#4A5E2E]/90 disabled:opacity-40 transition-colors">
                {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Descarregar proposta (PDF)
              </button>
            </div>

            {/* Guardar proposta + decisão */}
            {readOnly ? (
              <div data-testid="proposta-readonly-banner" className="rounded-xl border border-amber-300 bg-amber-50 p-4 mt-2">
                <p className="text-sm font-semibold text-amber-900">Apenas visualização</p>
                <p className="text-sm text-amber-800 mt-1">
                  {propostaEstado === "CONVERTIDA"
                    ? "Esta proposta já não é editável porque uma das encomendas geradas foi paga."
                    : propostaEstado === "CANCELADA"
                      ? "Esta proposta está cancelada/encerrada. Pode consultá-la, mas não editá-la."
                      : "Esta proposta está em modo de leitura."}
                </p>
                {encomendas.length > 0 && (
                  <p className="text-sm text-amber-900 mt-2" data-testid="proposta-readonly-encomendas">
                    Encomendas:{" "}
                    {encomendas.map((oid) => (
                      <a key={oid} href={`/my/encomendas/${oid}`} data-testid={`encomenda-link-${oid}`}
                        className="inline-block font-mono font-semibold text-amber-900 underline bg-white border border-amber-300 rounded px-1.5 py-0.5 mr-1">#{oid}</a>
                    ))}
                  </p>
                )}
              </div>
            ) : (
            <div data-testid="guardar-proposta-bloco" className="rounded-xl border border-[#E5E1D6] bg-white p-4 mt-2">
              <p className="text-sm font-semibold text-[#0F1829] mb-3">{editId ? "Atualizar esta proposta" : "Queres guardar esta proposta e decidir?"}</p>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <label className="text-sm text-[#0F1829]">
                  <span className="block text-xs text-[#6B7280] mb-1">Decisão</span>
                  <select data-testid="save-modo" value={saveModo} onChange={(e) => setSaveModo(e.target.value)}
                    className="rounded-lg border border-[#E5E1D6] px-3 py-2 text-sm bg-white">
                    <option value="agora">Vou decidir já</option>
                    <option value="mais_tempo">Preciso de mais tempo</option>
                  </select>
                </label>
                {saveModo === "mais_tempo" && (
                  <label className="text-sm text-[#0F1829]">
                    <span className="block text-xs text-[#6B7280] mb-1">Data prevista de decisão (máx. 90 dias)</span>
                    <input data-testid="save-data-decisao" type="date" value={dataDecisao} min={minDataDecisao} max={maxDataDecisao}
                      onChange={(e) => setDataDecisao(e.target.value)}
                      className="rounded-lg border border-[#E5E1D6] px-3 py-2 text-sm bg-white" />
                  </label>
                )}
                <button data-testid="btn-guardar-proposta" onClick={guardarProposta} disabled={saveState === "loading" || saveState === "done"}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0F1829] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#0F1829]/90 disabled:opacity-40 transition-colors">
                  {saveState === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {editId ? "Guardar alterações" : "Guardar proposta"}
                </button>
              </div>
              {saveState === "done" && <p data-testid="save-ok" className="text-sm text-[#4A5E2E] font-medium mt-2">Proposta guardada! A abrir "As minhas propostas"…</p>}
              {saveState === "error" && <p data-testid="save-err" className="text-sm text-rose-600 mt-2">Não foi possível guardar. Verifica a data de decisão (máx. 90 dias).</p>}
            </div>
            )}

            {/* Consultor Associado + pedido de contacto */}
            {proposta.consultor?.nome ? (
              <div data-testid="consultor-bloco" className="rounded-xl border border-[#E5E1D6] bg-white p-4 mt-2">
                <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-2">O teu Consultor de Excelência Pessoal</div>
                <div className="text-sm text-[#0F1829] font-semibold">{proposta.consultor.nome}</div>
                <div className="text-sm text-[#6B7280]">{proposta.consultor.telefone || "—"} · {proposta.consultor.email || "—"}</div>
                <div className="mt-3 border-t border-[#E5E1D6] pt-3">
                  <p className="text-sm font-semibold text-[#0F1829]">Pretendes que o teu Consultor de Excelência Pessoal te contacte?</p>
                  {leadState === "done" ? (
                    <div data-testid="lead-ok" className="mt-2 flex items-start gap-2 text-sm text-[#4A5E2E] font-medium">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>Muito Obrigado. Encaminhamos esta informação para o teu Consultor que irá entrar em contacto em breve.<br />Continuação de um Excelente Dia.</span>
                    </div>
                  ) : (
                    <button data-testid="btn-pedir-contacto" onClick={pedirContacto} disabled={leadState === "loading"}
                      className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#0F1829] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#0F1829]/90 disabled:opacity-40 transition-colors">
                      {leadState === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <User className="w-4 h-4" />}
                      Sim, quero ser contactado
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </section>
      )}
    </div>
  );
}

function FormadorAvatar({ nome, url, active }) {
  const [failed, setFailed] = useState(false);
  const initials = (nome || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const ring = active ? "ring-2 ring-[#0F1829]" : "ring-1 ring-[#E5E1D6]";
  if (url && !failed) {
    return (
      <img src={url} alt={nome} onError={() => setFailed(true)}
        className={`w-16 h-16 rounded-full object-cover ${ring}`} />
    );
  }
  return (
    <div className={`w-16 h-16 rounded-full flex items-center justify-center bg-[#4A5E2E] text-white text-lg font-semibold ${ring}`}>
      {initials}
    </div>
  );
}

function Line({ label, value, muted }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={muted ? "text-[#6B7280]" : "text-[#0F1829]"}>{label}</span>
      <span className={muted ? "text-[#6B7280]" : "text-[#0F1829] font-medium tabular-nums"}>{value}</span>
    </div>
  );
}
